import React from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import type { AdminUserDetail } from '../../types/api/admin-user';
import { formatDateForDisplay, formatDateTimeForDisplay } from '../../types/api/admin-user';

export interface UserDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUserDetail | null;
  onEdit?: (user: AdminUserDetail) => void;
  onDelete?: (user: AdminUserDetail) => void;
  onRestore?: (user: AdminUserDetail) => void;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  isOpen,
  onClose,
  user,
  onEdit,
  onDelete,
  onRestore,
}) => {
  // Show loading state if modal is open but user data is not yet loaded
  if (isOpen && !user) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="User Details"
        size="lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EAB308] mx-auto"></div>
            <p className="text-sm text-gray-600 mt-4">Loading user details...</p>
          </div>
        </div>
      </Modal>
    );
  }
  
  if (!user) return null;

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'FREE':
        return 'bg-gray-100 text-gray-800';
      case 'PREMIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'VIP':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getGenderBadgeColor = (gender: string) => {
    const colors: Record<string, string> = {
      'MALE': 'bg-blue-100 text-blue-800',
      'FEMALE': 'bg-pink-100 text-pink-800',
      'OTHER': 'bg-purple-100 text-purple-800',
      'PREFER_NOT_TO_SAY': 'bg-gray-100 text-gray-800',
    };
    return colors[gender] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="User Details"
      size="lg"
    >
      <div className="space-y-6">
        {/* User Profile Section */}
        <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {user.profileImageUrl ? (
              <img
                src={user.profileImageUrl}
                alt={user.name}
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#EAB308] to-[#CA8A04] flex items-center justify-center text-white text-2xl font-semibold">
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* User Basic Info */}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900">{user.name}</h3>
            <p className="text-sm text-gray-600 mt-1">{user.email}</p>
            <div className="flex gap-2 mt-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierBadgeColor(user.tier)}`}>
                {user.tier}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getGenderBadgeColor(user.gender)}`}>
                {user.gender.charAt(0) + user.gender.slice(1).toLowerCase().replace('_', ' ')}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {user.isActive ? 'Active' : 'Inactive'}
              </span>
              {user.isDeleted && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Deleted
                </span>
              )}
            </div>
          </div>
        </div>

        {/* User Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Contact Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Phone</label>
                <p className="text-sm text-gray-900">{user.phone || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Date of Birth</label>
                <p className="text-sm text-gray-900">{formatDateForDisplay(user.dateOfBirth)}</p>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Account Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Registration Date</label>
                <p className="text-sm text-gray-900">{formatDateTimeForDisplay(user.registrationDate)}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Last Login</label>
                <p className="text-sm text-gray-900">{formatDateTimeForDisplay(user.lastLogin)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Audit Information */}
        <div className="pt-6 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Audit Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500">Created At</label>
                  <p className="text-sm text-gray-900">{formatDateTimeForDisplay(user.createdAt)}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Created By</label>
                  <p className="text-sm text-gray-900">{user.createdBy || 'Self-registered'}</p>
                </div>
              </div>
            </div>
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500">Updated At</label>
                  <p className="text-sm text-gray-900">{formatDateTimeForDisplay(user.updatedAt)}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Updated By</label>
                  <p className="text-sm text-gray-900">{user.updatedBy || '-'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Deletion Information (if deleted) */}
          {user.isDeleted && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h5 className="text-sm font-semibold text-red-900 mb-2">Deletion Information</h5>
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-red-700">Deleted At</label>
                  <p className="text-sm text-red-900">{formatDateTimeForDisplay(user.deletedAt)}</p>
                </div>
                <div>
                  <label className="text-xs text-red-700">Deleted By</label>
                  <p className="text-sm text-red-900">{user.deletedBy || '-'}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {!user.isDeleted ? (
            <>
              {onEdit && (
                <Button variant="primary" onClick={() => onEdit(user)}>
                  Edit User
                </Button>
              )}
              {onDelete && (
                <Button variant="danger" onClick={() => onDelete(user)}>
                  Delete User
                </Button>
              )}
            </>
          ) : (
            onRestore && (
              <Button variant="primary" onClick={() => onRestore(user)}>
                Restore User
              </Button>
            )
          )}
        </div>
      </div>
    </Modal>
  );
};

